import React, { useEffect, useState } from "react";
import { Box, Flex } from "rebass/styled-components";

import { LabelReadOnly } from "../../../../Components/Form/Label";
import { Button } from "../../../../Components/Button";
import { useIsDesktopApp } from "../../../../Hooks/useIsDesktopApp";
import { LinkedAccountsViewModel } from "../../ViewModel";

import {
  DisabledButton,
  IconWrapper,
  StyledRiot,
  UnlinkButton,
} from "../styledComponents";
import { useHistory } from "react-router-dom";
import { URLS } from "../../../../Utilities/Maps";
import { useUser } from "../../../../Hooks/useUser";
import { DropdownSelector } from "../../../../Components/RiotRegionSelector/DropdownSelector";
import { Error } from "../../../../Components/Inputs";
import { getRiotSelectionData } from "../../../../Components/RiotRegionSelector/utils";
import { analytics } from "MVVM/Analytics";

const { REACT_APP_BASE_URL } = process.env;

const LinkRiot = ({ buttonText }: { buttonText: string }) => {
  const { unlinkAccount, updateRiotRegion } = LinkedAccountsViewModel();
  const { allstarUser, getFreshProfile } = useUser();
  const riotUsedForSignup = allstarUser?.user?.riot?.usedForSignUp;
  const { isDesktop } = useIsDesktopApp();
  const [linked, setLinked] = useState(!!allstarUser?.user?.riot?.active);
  const history = useHistory();
  const [selectedValue, setSelectedValue] = useState(
    allstarUser?.user?.riot?.region || "NONE",
  );
  const riotSelectionData = getRiotSelectionData();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [dropdownDisabled, setDropdownDisabled] = useState(
    !!allstarUser?.user?.riot?.region,
  );

  const [labelText, setLabelText] = useState(
    selectedValue === "NONE"
      ? "Region"
      : `${riotSelectionData.find(
          (region) => region.valueToSave === selectedValue,
        )?.valueToSave}`,
  );

  const saveRiotRegion = async (region: string) => {
    const response = await updateRiotRegion({ variables: { region } });
    analytics.track("Riot Region Updated", {
      region,
    });

    const {
      data: { editRiotRegion },
    } = response;

    if (editRiotRegion?.errors?.length) {
      const error = editRiotRegion.errors[0];
      if (error.field === "User" && error.code === "NOT_FOUND") {
        setErrorMessage("Your Riot account was not found in that region");
        return { success: false };
      } else {
        setErrorMessage("Something went wrong.");
        return { success: false };
      }
    }
    setErrorMessage("");
    await getFreshProfile();
    return { success: true };
  };
  const link = async (id: string, isDesktopApp: string | boolean | null) => {
    if (isDesktop) {
      window.parent.postMessage(
        {
          type: "openLink",
          link: `${REACT_APP_BASE_URL}${URLS.AUTH_ROUTES.RIOT}?flow=link&referrer=DESKTOP_APP`,
        },
        "*",
      );
      return;
    }

    history.push(
      `${URLS.AUTH_ROUTES.RIOT}?flow=link&referrer=CONNECTED_ACCOUNTS`,
    );
  };

  const unlink = async () => {
    await unlinkAccount({ variables: { accountType: "riot" } });
    setLinked(!linked);
  };

  useEffect(() => {
    if (selectedValue === "NONE") setLabelText("Region");
    else
      setLabelText(
        `${riotSelectionData.find(
          (region) => region.valueToSave === selectedValue,
        )?.valueToSave}`,
      );
  }, [selectedValue, riotSelectionData]);

  return (
    <Flex flexDirection={"column"} style={{ gap: 20 }}>
      <Flex pt={5} alignItems="center">
        <IconWrapper>
          <StyledRiot />
        </IconWrapper>
        {linked ? (
          <Flex width="100%" style={{ gap: "8px" }} alignItems={"center"}>
            <DropdownSelector
              inline={true}
              onChange={async (region) => {
                setSelectedValue(region);
                setDropdownDisabled(true);
                analytics.track("Riot Region Updated", {
                  region,
                });
                const { success } = await saveRiotRegion(region);
                if (!success) {
                  setSelectedValue("NONE");
                  setDropdownDisabled(false);
                }
              }}
              disabled={dropdownDisabled}
              values={riotSelectionData}
              labelText={labelText}
              noSelection={selectedValue === "NONE"}
            />

            <LabelReadOnly
              label={"Riot"}
              text={allstarUser.user?.riot?.riotId}
            />
            {riotUsedForSignup ? (
              <DisabledButton
                size="small"
                flex={true}
                maxWidth={2}
                classification="secondary"
                title="You cannot unlink your primary login account."
                disabled
              >
                Unlink
              </DisabledButton>
            ) : (
              <UnlinkButton
                size="small"
                flex={true}
                maxWidth={2}
                classification="secondary"
                onClick={() => unlink()}
                flexShrink={0}
              >
                Unlink
              </UnlinkButton>
            )}
          </Flex>
        ) : (
          <Button
            size="small"
            flex={true}
            maxWidth={2}
            width="100%"
            classification="secondary"
            target="_blank"
            onClick={() => link(allstarUser.user._id, isDesktop)}
          >
            {buttonText}
          </Button>
        )}
      </Flex>
      {errorMessage && <Error>{errorMessage}</Error>}
    </Flex>
  );
};

export { LinkRiot };
